import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToasts } from 'react-toast-notifications';
import { useParams } from 'react-router';
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import moment from 'moment';

const NimoclePointMagnification = (props) => {
  const { user } = props;
  const { siteId, mallId } = useParams();
  const [magnificationData, setMagnificationData] = useState(null);
  const [lockData, setLockData] = useState(false);
  const { addToast } = useToasts();

  const initialValues = {
    site_id: siteId,
    mall_id: mallId,
    nimocle_point_magnification_id: null,
    magnification: '',
    start_date: null,
    end_date: null,
    upd_date: null,
  };
  const [values, setValues] = useState(initialValues);

  const refreshData = useCallback(async () => {
    setLockData(true);
    try {
      const jwt = localStorage.getItem('jwt');
      const resdata = (await axios.post(`${process.env.REACT_APP_BACKEND_URL}/nimocle_point_magnification/search/`,
        { site_id: siteId, mall_id: mallId },
        { headers: { Authorization: `Bearer ${jwt}` } }
      )).data;
      setMagnificationData(Array.isArray(resdata) ? resdata : []);
    } catch (error) {
      console.error(error);
      setMagnificationData([]);
    } finally {
      setLockData(false);
    }
  }, [siteId, mallId]);

  /* 登録・更新 */
  const handleRegistClick = async () => {
    const jwt = localStorage.getItem('jwt');
    if (!values.magnification || !(String(values.magnification)).match(/^[1-9][0-9]*$/)) {
      window.alert('ポイント倍率は正の半角整数で入力してください。');
      return;
    }
    if (!values.start_date || !values.end_date) {
      window.alert('適用期間を入力してください。');
      return;
    }
    if (moment(values.start_date) > moment(values.end_date)) {
      window.alert('適用期間が間違っています。');
      return;
    }

    let overlapping;
    try {
      overlapping = (await axios.post(`${process.env.REACT_APP_BACKEND_URL}/nimocle_point_magnification/check_overlap/`, {
        site_id: siteId,
        mall_id: mallId,
        start_date: values.start_date,
        end_date: values.end_date,
        nimocle_point_magnification_id: values.nimocle_point_magnification_id,
      }, { headers: { Authorization: `Bearer ${jwt}` } })).data;
    } catch (err) {
      console.log(err);
      return;
    }

    // 既存のポイントキャンペーン(m_point_campaign)は重複を警告のみで続行可とするが、
    // こちらは倍率適用の一意性を保つため、重複があれば登録自体をブロックする。
    if (Array.isArray(overlapping) && overlapping.length > 0) {
      window.alert('対象期間が他のキャンペーン設定と重複しています。期間を見直してください。');
      return;
    }

    if (!window.confirm('登録してよろしいですか？')) {
      return;
    }

    const params = {
      ...values,
      site_id: siteId,
      mall_id: mallId,
      upd_name: user.userId,
    };

    let res;
    try {
      setLockData(true);
      res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/nimocle_point_magnification/`, params, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
    } catch (err) {
      console.log(err);
      addToast('登録に失敗しました。', { appearance: 'error', autoDismiss: true });
    } finally {
      if (res) {
        if (!res.data.result) {
          addToast(res.data.msg, { appearance: 'error', autoDismiss: true });
        } else {
          addToast('登録しました。', { appearance: 'success', autoDismiss: true });
          await refreshData();
          handleResetClick();
        }
      }
      setLockData(false);
    }
  };

  /* 削除 */
  const handleDeleteClick = async (data) => {
    const jwt = localStorage.getItem('jwt');
    if (!window.confirm(`ポイント倍率設定[ID:${data.nimocle_point_magnification_id}]を削除します。\nよろしいですか？`)) {
      return;
    }
    const params = {
      site_id: siteId,
      nimocle_point_magnification_id: data.nimocle_point_magnification_id,
      upd_date: data.upd_date,
    };
    let res;
    try {
      setLockData(true);
      res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/nimocle_point_magnification/delete`, params, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
    } catch (err) {
      console.log(err);
      addToast('削除に失敗しました。', { appearance: 'error', autoDismiss: true });
    } finally {
      if (res) {
        if (!res.data.result) {
          addToast(res.data.msg, { appearance: 'error', autoDismiss: true });
        } else {
          addToast('削除しました。', { appearance: 'success', autoDismiss: true });
          await refreshData();
          handleResetClick();
        }
      }
      setLockData(false);
    }
  };

  /* 編集ボタン押下 */
  const handleUpdateClick = (data) => {
    setValues({
      site_id: siteId,
      mall_id: mallId,
      nimocle_point_magnification_id: data.nimocle_point_magnification_id,
      magnification: data.magnification,
      start_date: data.start_date,
      end_date: data.end_date,
      upd_date: data.upd_date,
    });
  };

  /* リセットボタン押下 */
  const handleResetClick = () => {
    setValues(initialValues);
  };

  const handleChangeDate = (name, value) => {
    if (!value) {
      setValues({ ...values, [name]: null });
      return;
    }
    // 終了日は23:59:59、開始日は00:00:00にする（m_point_campaign同様の扱い）
    value = name === 'end_date' ? moment(value).endOf('day').toDate() : moment(value).startOf('day').toDate();
    setValues({ ...values, [name]: value });
  };

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const renderList = () => (
    <div className="row mb-3 mt-3 p-0">
      <div className="col-12 p-0">
        <table className="table table-bordered table-striped">
          <thead className="table-info">
            <tr>
              <td className="text-center align-middle">ID</td>
              <td className="text-center align-middle">ポイント倍率</td>
              <td className="text-center align-middle">適用期間</td>
              <td className="text-center align-middle">処理</td>
            </tr>
          </thead>
          <tbody>
            {magnificationData.length === 0 && <tr><td colSpan={4} className="text-center">データがありません</td></tr>}
            {magnificationData.map((data) => (
              <tr key={data.nimocle_point_magnification_id}>
                <td className="text-center align-middle">{data.nimocle_point_magnification_id}</td>
                <td className="text-center align-middle">{data.magnification}倍</td>
                <td className="text-center align-middle">
                  {moment(data.start_date).format('YYYY/MM/DD')}～{moment(data.end_date).format('YYYY/MM/DD')}
                </td>
                <td className="text-center align-middle text-nowrap">
                  <button className="btn btn-primary mx-1" onClick={() => handleUpdateClick(data)}>編集</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderInput = () => (
    <div className="mx-1">
      <div className="row mb-3">
        <div className="col-2 text-center align-self-center">
          <span>ID</span>
        </div>
        <div className="col-2 text-center align-self-center">
          <input type="text" className="form-control" value={values.nimocle_point_magnification_id || ''} placeholder="新規" readOnly />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-2 text-center align-self-center">
          <span>ポイント倍率</span>
        </div>
        <div className="col-3 text-center align-self-center">
          <input type="text" className="form-control" value={values.magnification} onChange={(e) => setValues({ ...values, magnification: e.target.value })} />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-2 text-center align-self-center">
          <label htmlFor="start_date">適用開始日</label>
        </div>
        <div className="col-3 text-center align-self-center">
          <DatePicker className="custom-select w-100 text-center border-textbox text-primary font-weight-bold" locale="ja" dateFormat="yyyy/MM/dd" id="start_date" name="start_date"
            selected={values.start_date ? new Date(values.start_date) : null}
            maxDate={values.end_date ? new Date(values.end_date) : null}
            onChange={(date) => handleChangeDate('start_date', date)}
            placeholderText="- 選択 -"
            onFocus={(e) => e.target.blur()}
          />
        </div>
        <div className="col-2 text-center align-self-center">
          <label htmlFor="end_date">適用終了日</label>
        </div>
        <div className="col-3 text-center align-self-center">
          <DatePicker className="custom-select w-100 text-center border-textbox text-primary font-weight-bold" locale="ja" dateFormat="yyyy/MM/dd" id="end_date" name="end_date"
            selected={values.end_date ? new Date(values.end_date) : null}
            minDate={values.start_date ? new Date(values.start_date) : null}
            onChange={(date) => handleChangeDate('end_date', date)}
            placeholderText="- 選択 -"
            onFocus={(e) => e.target.blur()}
          />
        </div>
      </div>
      <div className="row mb-2 pb-4">
        <div className="col-3 text-center">
          <button type="button" disabled={lockData} className="btn btn-primary mx-1 w-50" onClick={handleRegistClick}>登録</button>
        </div>
        <div className="col-3 text-center">
          <button type="button" disabled={lockData || values.nimocle_point_magnification_id === null} className="btn btn-primary mx-1 w-50" onClick={() => handleDeleteClick(values)}>削除</button>
        </div>
        <div className="col-3 text-center">
          <button type="button" className="btn btn-primary mx-1 w-50" onClick={handleResetClick}>リセット</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container">
      {magnificationData === null && <div className="row"><div className="col-12">読み込み中・・・</div></div>}
      {magnificationData !== null && renderList()}
      {renderInput()}
    </div>
  );
};

export default NimoclePointMagnification;
